<script setup>
/*
 * "Se også" nederst på produktsiden.
 *
 * Afløser to ProductLinkList med rene tekstlinks. De var der for link-flowet —
 * 77.228 produktsider havde nul udgående links til andre varer — men de så ud
 * som en fodnote, og ingen klikkede på dem.
 *
 * Samme links, samme antal, som kort med billede og pris. Varerne her er dem
 * sidebaren IKKE viser; delingen sker i controlleren, så den samme vare ikke
 * står to steder på siden.
 *
 * SSR-sikker: ingen window, ingen onMounted. Et billede der fejler skjules,
 * men pladsen bliver stående, så kortene ikke får forskellig højde.
 */
import { produktnavn } from '@/Composables/produktnavn';
import { Link } from '@inertiajs/vue3';
import { ref } from 'vue';

defineProps({
    varer: { type: Array, default: () => [] },
    overskrift: { type: String, default: 'Se også' },
});

const fejlede = ref(new Set());

const prisTekst = (v) => v.lowest_price
    ? `${Number(v.lowest_price).toLocaleString('da-DK', { maximumFractionDigits: 0 })} kr.`
    : null;
</script>

<template>
    <section v-if="varer.length" class="card mb-4 shadow-sm se-ogsaa">
        <div class="card-body py-4 px-4">
            <h2 class="h5 mb-3">{{ overskrift }}</h2>

            <div class="row row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-xl-6 g-3">
                <div v-for="v in varer" :key="v.id" class="col">
                    <Link :href="'/produkt/' + v.slug" class="se-kort text-decoration-none h-100">
                        <span class="se-billede">
                            <picture v-if="v.image_id && !fejlede.has(v.id)">
                                <!--
                                    TO OPLOESNINGER, ikke én.
                                    
                                    MAALT 16. oktober 2026: billedet blev vist i
                                    160px og hentet i 160px — faktor 1,00 ved alle
                                    bredder fra 1024 til 1920. Paa en 2x-skaerm er
                                    det halv oploesning, og kortene saa uskarpe ud
                                    ved siden af hovedbilledet, som henter 640px til
                                    en 336px kasse.
                                    
                                    1x-skaerme henter stadig den lille fil.
                                -->
                                <source
                                    :srcset="`/images/${v.image_id}?w=160&fmt=webp 1x, /images/${v.image_id}?w=320&fmt=webp 2x`"
                                    type="image/webp"
                                />
                                <img
                                    :src="`/images/${v.image_id}?w=160`"
                                    :srcset="`/images/${v.image_id}?w=160 1x, /images/${v.image_id}?w=320 2x`"
                                    :alt="produktnavn(v)"
                                    width="160"
                                    height="160"
                                    loading="lazy"
                                    decoding="async"
                                    @error="fejlede.add(v.id)"
                                >
                            </picture>
                        </span>
                        <span class="se-navn">{{ produktnavn(v) }}</span>
                        <span v-if="prisTekst(v)" class="se-pris">{{ prisTekst(v) }}</span>
                    </Link>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.se-kort {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    padding: 0.6rem;
    border: 1px solid var(--pc-border, rgba(255, 255, 255, 0.08));
    border-radius: var(--pc-radius);
    background: var(--pc-bg-tertiary);
    transition: border-color 0.15s ease, transform 0.15s ease;
}

.se-kort:hover {
    border-color: var(--pc-accent);
    transform: translateY(-2px);
}

.se-billede {
    /*
     * Fast kvadrat. Uden det bestemmer billedets egne mål kortets højde, og
     * en række med et højt og et bredt billede bliver ujævn. Pladsen holdes
     * også når billedet mangler eller fejler.
     */
    aspect-ratio: 1 / 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-radius: var(--pc-radius);
    overflow: hidden;
}

.se-billede img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.se-navn {
    font-size: 0.8rem;
    line-height: 1.25;
    color: var(--pc-text-primary);
    /* To linjer og så afkortning: varenavne er lange, kortene er smalle. */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.se-pris {
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--pc-price, var(--pc-accent));
    margin-top: auto;
}
</style>
